<template>
  <div class="detail-split">
    <aside class="seller-list">
      <div class="seller-list__search">
        <v-icon small color="grey">mdi-magnify</v-icon>
        <input
          :value="search"
          placeholder="Pesquise.."
          class="seller-list__input"
          @input="$emit('update:search', $event.target.value)"
        />
      </div>
      <div class="seller-list__month">
        <v-btn icon x-small @click="$emit('prev-month')"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <slot name="month" />
        <v-btn icon x-small @click="$emit('next-month')"><v-icon>mdi-chevron-right</v-icon></v-btn>
        <v-spacer />
        <v-btn icon x-small color="primary" @click="$emit('add-seller')">
          <v-icon small>mdi-account-plus</v-icon>
        </v-btn>
      </div>
      <div class="seller-list__divider" />
      <div class="seller-list__items">
        <div
          v-for="row in filteredRows"
          :key="row.employee_id"
          class="seller-list__item"
          :class="{ 'seller-list__item--active': selected && selected.employee_id === row.employee_id }"
          @click="$emit('select', row)"
        >
          <div class="seller-avatar">{{ initials(row.employee_name) }}</div>
          <div class="seller-list__meta">
            <div class="seller-list__name">{{ row.employee_name }}</div>
            <div class="seller-list__resumo">{{ resumo(row) }}</div>
          </div>
          <v-btn icon x-small class="seller-list__remove" @click.stop="$emit('remove-seller', row)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="!filteredRows.length" class="pa-4 caption grey--text text-center">
          Nenhum vendedor encontrado
        </div>
      </div>
    </aside>

    <section class="seller-form" v-if="selected">
      <div class="seller-form__header">
        <div class="seller-form__identity">
          <div class="seller-avatar seller-avatar--lg">{{ initials(selected.employee_name) }}</div>
          <div class="seller-form__identity-text">
            <div class="seller-form__title">{{ selected.employee_name }}</div>
            <div class="seller-form__sub">{{ monthLabel }} · {{ resumo(selected) }}</div>
          </div>
        </div>
        <div v-if="editing" class="seller-form__actions">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined color="primary" small v-bind="attrs" v-on="on">
                <v-icon left small>mdi-content-copy</v-icon>
                Copiar de...
              </v-btn>
            </template>
            <v-list dense max-height="320" style="overflow: auto">
              <v-subheader>Copiar configuração de:</v-subheader>
              <v-list-item
                v-for="row in copySources"
                :key="row.employee_id"
                @click="$emit('copy-seller', row)"
              >
                <v-list-item-title>{{ row.employee_name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn outlined color="blue-grey" small :loading="copying" @click="$emit('copy-month')">
            <v-icon left small>mdi-history</v-icon>
            Mês anterior
          </v-btn>
          <f-btn cancel :disabled="saving" @click="$emit('cancel-edit')" />
          <f-btn save :disabled="!selected._dirty || saving" :loading="saving" @click="$emit('save-selected')" />
        </div>
      </div>

      <div class="seller-form__body" :class="{ 'is-readonly': !editing }">
        <v-expansion-panels v-model="panels" multiple flat class="seller-panels">
          <v-expansion-panel class="seller-panel">
            <v-expansion-panel-header class="seller-panel__header">
              <div class="d-flex align-center">
                <v-icon small color="red" class="mr-2">mdi-percent</v-icon>
                <span>Comissões Fixas</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="fields-grid">
                <div class="field">
                  <div class="field__label">Tipo Comissão Cimento</div>
                  <select v-model="selected.cement_comission_type" class="field__select" :disabled="!editing" @change="markDirty">
                    <option value="per_bag">Por saco (R$)</option>
                    <option value="per_revenue">Por faturamento (%)</option>
                  </select>
                </div>
                <div class="field">
                  <div class="field__label">Comissão Cimento</div>
                  <div class="field__input-wrap">
                    <span v-if="selected.cement_comission_type !== 'per_revenue'" class="field__pre">R$</span>
                    <input v-model.number="selected.cement_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                    <span class="field__suf">{{ selected.cement_comission_type === 'per_revenue' ? '% fat.' : '/saco' }}</span>
                  </div>
                </div>
                <div class="field">
                  <div class="field__label">Marca Fassa</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected.fassa_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
                <div class="field">
                  <div class="field__label">Outros Produtos</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected.other_products_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
                <div class="field">
                  <div class="field__label">Cal Galpão</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected.yard_cal_fixed_comission" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="seller-panel">
            <v-expansion-panel-header class="seller-panel__header">
              <div class="d-flex align-center">
                <v-icon small color="red" class="mr-2">mdi-trending-up</v-icon>
                <span>Comissão Lucratividade</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="fields-grid">
                <div class="field" v-for="f in profitabilityFields" :key="f.key">
                  <div class="field__label">{{ f.label }}</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected[f.key]" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="seller-panel">
            <v-expansion-panel-header class="seller-panel__header">
              <div class="d-flex align-center">
                <v-icon small color="red" class="mr-2">mdi-flag</v-icon>
                <span>Metas</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="field-group-label">VOLUME — COMISSÃO ADICIONAL</div>
              <div class="fields-grid mb-4">
                <div class="field" v-for="f in volumeFields" :key="f.key">
                  <div class="field__label">{{ f.label }}</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected[f.key]" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
              <div class="field-group-label">CLIENTES</div>
              <div class="fields-grid mb-4">
                <div class="field">
                  <div class="field__label">Tipo</div>
                  <select v-model="selected.client_goal_type" class="field__select" :disabled="!editing" @change="markDirty">
                    <option value="fixed">Fixo</option>
                    <option value="variable">Variável</option>
                  </select>
                </div>
                <div class="field" v-for="f in clientFields" :key="f.key">
                  <div class="field__label">{{ f.label }}</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected[f.key]" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
              <div class="field-group-label">LUCRO</div>
              <div class="fields-grid">
                <div class="field" v-for="f in profitFields" :key="f.key">
                  <div class="field__label">{{ f.label }}</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected[f.key]" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="seller-panel">
            <v-expansion-panel-header class="seller-panel__header">
              <div class="d-flex align-center">
                <v-icon small color="red" class="mr-2">mdi-star</v-icon>
                <span>Bônus</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="fields-grid">
                <div class="field">
                  <div class="field__label">Clientes Reativados</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected.reactivated_clients_bonus" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
                <div class="field">
                  <div class="field__label">Novos Clientes</div>
                  <div class="field__input-wrap">
                    <span class="field__pre">R$</span>
                    <input v-model.number="selected.new_clients_bonus" class="field__input" type="number" step="any" :readonly="!editing" @input="markDirty" />
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="seller-panel">
            <v-expansion-panel-header class="seller-panel__header">
              <div class="d-flex align-center">
                <v-icon small color="red" class="mr-2">mdi-history</v-icon>
                <span>Histórico de Alterações</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="historyLoading" class="pa-3 caption grey--text">Carregando histórico…</div>
              <div v-else-if="!history.length" class="pa-3 caption grey--text">Sem alterações registradas</div>
              <div v-for="h in history" :key="h.id" class="history-row">
                <div class="history-row__dt">{{ h.dt }}</div>
                <div class="history-row__user">{{ h.user }}</div>
                <div class="history-row__txt">{{ h.txt }}</div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </section>

    <section v-else class="seller-form seller-form--empty">
      <div class="caption grey--text">Selecione um vendedor na lista</div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SellerComissionFormView',
  props: {
    rows: { type: Array, required: true },
    selected: { type: Object, default: null },
    search: { type: String, default: '' },
    monthLabel: { type: String, required: true },
    editing: { type: Boolean, default: false },
    history: { type: Array, default: () => [] },
    historyLoading: { type: Boolean, default: false },
    copying: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    resumoFn: { type: Function, required: true },
    initialsFn: { type: Function, required: true },
  },
  data() {
    return {
      panels: [0, 1, 2, 3, 4],
      profitabilityFields: [
        { key: 'profitability_bonus', label: 'Bônus' },
        { key: 'profitability_margin_increase', label: 'Aumento de Margem' },
        { key: 'profitability_yard_margin', label: 'Margem Galpão' },
        { key: 'profitability_gc_margin', label: 'Margem GC' },
      ],
      volumeFields: [
        { key: 'volume_goal', label: 'Meta' },
        { key: 'volume_super_goal', label: 'Super Meta' },
        { key: 'volume_hyper_goal', label: 'Hiper Meta' },
      ],
      clientFields: [
        { key: 'client_goal', label: 'Meta' },
        { key: 'client_super_goal', label: 'Super Meta' },
        { key: 'client_hyper_goal', label: 'Hiper Meta' },
      ],
      profitFields: [
        { key: 'profit_goal', label: 'Meta' },
        { key: 'profit_super_goal', label: 'Super Meta' },
        { key: 'profit_hyper_goal', label: 'Hiper Meta' },
      ],
    }
  },
  computed: {
    filteredRows() {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return this.rows
      return this.rows.filter((r) => (r.employee_name || '').toLowerCase().includes(q))
    },
    copySources() {
      if (!this.selected) return this.rows
      return this.rows.filter((r) => r.employee_id !== this.selected.employee_id)
    },
  },
  methods: {
    initials(name) { return this.initialsFn(name) },
    resumo(row) { return this.resumoFn(row) },
    markDirty() {
      if (!this.editing || !this.selected) return
      this.selected._dirty = true
      this.$emit('dirty')
    },
  },
}
</script>

<style scoped>
.detail-split {
  display: flex;
  align-items: stretch;
  min-height: 680px;
}
.seller-list {
  width: 300px;
  flex: none;
  border-right: 1px solid #e0e0e0;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.seller-list__search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px 8px;
}
.seller-list__input {
  flex: 1;
  border: none;
  border-bottom: 1px solid #bdbdbd;
  outline: none;
  font-size: 13px;
  padding: 4px 2px;
  background: transparent;
}
.seller-list__month {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px 8px;
}
.seller-list__divider { height: 1px; background: #eeeeee; }
.seller-list__items { overflow: auto; flex: 1; }
.seller-list__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px 9px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  border-left: 3px solid transparent;
}
.seller-list__item:hover { background: #f5f5f5; }
.seller-list__item--active {
  border-left-color: #e53935;
  background: #fbe9e7;
}
.seller-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #546e7a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  flex: none;
}
.seller-avatar--lg {
  width: 40px;
  height: 40px;
  background: #455a64;
  font-size: 14px;
}
.seller-list__meta { min-width: 0; flex: 1; }
.seller-list__name {
  font-size: 13px;
  font-weight: 500;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.seller-list__remove {
  flex: none;
  opacity: 0.35;
}
.seller-list__item:hover .seller-list__remove,
.seller-list__item--active .seller-list__remove {
  opacity: 1;
}
.seller-list__resumo { font-size: 11px; color: #78909c; margin-top: 1px; }
.seller-form {
  flex: 1;
  min-width: 0;
  background: #fafafa;
  display: flex;
  flex-direction: column;
}
.seller-form--empty {
  align-items: center;
  justify-content: center;
}
.seller-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  padding: 16px 22px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
}
.seller-form__identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1 1 220px;
}
.seller-form__identity-text {
  min-width: 0;
}
.seller-form__title {
  font-size: 16px;
  font-weight: 500;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.seller-form__sub {
  font-size: 12px;
  color: #757575;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.seller-form__actions {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
}
.seller-form__body { padding: 16px 22px 20px; overflow: auto; }
.seller-panels >>> .v-expansion-panel {
  border: 1px solid #e4e7ea;
  border-radius: 4px !important;
  margin-bottom: 14px;
  background: #fff;
}
.seller-panel__header {
  color: #e53935 !important;
  font-weight: 500;
  font-size: 13.5px;
}
.fields-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px 26px;
}
.field-group-label {
  font-size: 10px;
  font-weight: 500;
  color: #90a4ae;
  letter-spacing: 0.8px;
  margin: 0 0 8px;
}
.field__label {
  font-size: 10.5px;
  color: #757575;
  margin-bottom: 2px;
}
.field__input-wrap {
  display: flex;
  align-items: baseline;
  gap: 5px;
  border-bottom: 1px solid #b0bec5;
  padding: 3px 0 4px;
}
.field__pre, .field__suf { font-size: 12px; color: #78909c; }
.field__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  padding: 0;
}
.is-readonly .field__input,
.is-readonly .field__select {
  color: #607d8b;
  cursor: default;
}
.is-readonly .field__input-wrap,
.is-readonly .field__select {
  background: #fafafa;
}
.field__input:focus { background: #e3f2fd; }
.field__select {
  width: 100%;
  border: none;
  border-bottom: 1px solid #b0bec5;
  outline: none;
  font-size: 14px;
  background: transparent;
  padding: 3px 0 4px;
  cursor: pointer;
}
.field__input::-webkit-outer-spin-button,
.field__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.field__input[type='number'] { -moz-appearance: textfield; }
.history-row {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 9px 4px;
  border-bottom: 1px solid #f5f5f5;
}
.history-row__dt { width: 140px; flex: none; font-size: 11.5px; color: #90a4ae; }
.history-row__user { width: 170px; flex: none; font-size: 12px; color: #546e7a; }
.history-row__txt { font-size: 12.5px; color: #37474f; }
@media (max-width: 960px) {
  .detail-split { flex-direction: column; }
  .seller-list { width: 100%; max-height: 260px; }
  .fields-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
